import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";

import { tokenStore } from "@/lib/tokenStore";

/** Phase 3-sso — landing page the backend redirects to after a
 * successful (or failed) IdP callback.
 *
 * Two handoff shapes, picked by the backend's `SSO_TOKEN_HANDOFF`
 * setting:
 *
 *   - **fragment (default)** — tokens arrive in the URL fragment so
 *     they never hit webserver access logs:
 *       /sso/complete#access=...&refresh=...&org_id=...&new_user=0|1
 *
 *   - **cookie (Phase 3-sso-cookie)** — backend set httponly Secure
 *     cookies and redirected with a query-string flag (no token in
 *     either URL part):
 *       /sso/complete?via=cookie&org_id=...&new_user=0|1
 *     The SPA only has to invalidate ["me"]; the cookies travel on
 *     the next API call automatically.
 *
 * Error path is `#error=<reason>&detail=<msg>` in both modes.
 */
export default function SsoCompletePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<{ reason: string; detail: string } | null>(
    null,
  );

  useEffect(() => {
    // Cookie handoff path first — backend signalled via `?via=cookie`.
    const search = new URLSearchParams(window.location.search);
    if (search.get("via") === "cookie") {
      // Strip the marker from history (cookies still ride along on
      // every subsequent API call). No token plaintext to wipe here.
      window.history.replaceState(null, "", "/sso/complete");
      void qc.invalidateQueries({ queryKey: ["me"] });
      navigate("/dashboard", { replace: true });
      return;
    }

    // Default: fragment handoff.
    const frag = window.location.hash.replace(/^#/, "");
    if (!frag) {
      setError({ reason: "missing_fragment", detail: t("ui.ssoComplete.detailNoPayload") });
      return;
    }
    const params = new URLSearchParams(frag);
    const err = params.get("error");
    if (err) {
      setError({
        reason: err,
        detail: params.get("detail") || t("ui.ssoComplete.detailFallback"),
      });
      return;
    }
    const access = params.get("access");
    const refresh = params.get("refresh");
    if (!access || !refresh) {
      setError({
        reason: "incomplete_payload",
        detail: t("ui.ssoComplete.detailIncomplete"),
      });
      return;
    }
    tokenStore.setPair(access, refresh);
    // Clear the fragment from history so a back-button doesn't replay it.
    window.history.replaceState(null, "", "/sso/complete");
    void qc.invalidateQueries({ queryKey: ["me"] });
    navigate("/dashboard", { replace: true });
  }, [navigate, qc, t]);

  return (
    <main className="min-h-dvh bg-nb-bg py-10">
      <div className="card mx-auto mt-12 max-w-md space-y-4">
        <h1 className="text-xl font-semibold text-nb-text">
          {error ? t("ui.ssoComplete.failedTitle") : t("ui.ssoComplete.signingIn")}
        </h1>
        {error ? (
          <>
            <p className="text-sm text-cvp-danger" role="alert">
              {error.detail}
            </p>
            <p className="text-xs text-nb-text-3">{t("ui.ssoComplete.reasonLabel", { reason: error.reason })}</p>
            <a className="btn-ghost w-full text-center" href="/login">
              {t("ui.ssoComplete.backToSignIn")}
            </a>
          </>
        ) : (
          <p className="text-sm text-nb-text-2">
            {t("ui.ssoComplete.completing")}
          </p>
        )}
      </div>
    </main>
  );
}
